import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi, publicAssetUrl } from '../../lib/adminApi';

const initialForm = {
  title: '',
  summary: '',
  content: '',
  author: 'Federal Polytechnic Bida',
  status: 'draft',
  is_featured: false,
};

export default function AdminNewsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(initialForm);
  const [image, setImage] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadItem() {
      if (!isEdit) return;
      try {
        const data = await adminApi(`/api/admin/news/${id}`);
        setForm({
          title: data.title || '',
          summary: data.summary || '',
          content: data.content || '',
          author: data.author || 'Federal Polytechnic Bida',
          status: data.status || 'draft',
          is_featured: Boolean(data.is_featured),
        });
        setCurrentImage(data.image_url || '');
      } catch (err) {
        setError(err.message);
      }
    }
    loadItem();
  }, [id, isEdit]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function submitForm(e) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => body.append(key, value));
      if (image) body.append('image', image);

      await adminApi(isEdit ? `/api/admin/news/${id}` : '/api/admin/news', {
        method: isEdit ? 'PUT' : 'POST',
        body,
      });

      navigate('/adxm/news');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title={isEdit ? 'Edit News' : 'Create News'}
      subtitle="Write institutional news, save as draft, or publish immediately."
      actions={<Link to="/adxm/news" className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-100">Back to News</Link>}
    >
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}

      <form onSubmit={submitForm} className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <label className="block">
            <span className="text-sm font-black text-slate-700">Title</span>
            <input value={form.title} onChange={(e) => updateField('title', e.target.value)} required className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
          </label>

          <label className="block">
            <span className="text-sm font-black text-slate-700">Summary</span>
            <textarea value={form.summary} onChange={(e) => updateField('summary', e.target.value)} required rows="4" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
          </label>

          <label className="block">
            <span className="text-sm font-black text-slate-700">Full Content</span>
            <textarea value={form.content} onChange={(e) => updateField('content', e.target.value)} rows="12" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
          </label>
        </div>

        <aside className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <label className="block">
            <span className="text-sm font-black text-slate-700">Author</span>
            <input value={form.author} onChange={(e) => updateField('author', e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
          </label>

          <label className="block">
            <span className="text-sm font-black text-slate-700">Status</span>
            <select value={form.status} onChange={(e) => updateField('status', e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>

          <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 text-sm font-bold text-slate-700">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => updateField('is_featured', e.target.checked)} />
            Feature this news on top
          </label>

          <label className="block">
            <span className="text-sm font-black text-slate-700">Featured Image</span>
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
          </label>

          {currentImage && !image ? (
            <img src={publicAssetUrl(currentImage)} alt="Current news" className="h-44 w-full rounded-xl object-cover" />
          ) : null}

          <button disabled={saving} className="w-full rounded-xl bg-emerald-700 px-5 py-4 text-sm font-black text-white shadow hover:bg-emerald-800 disabled:opacity-60">
            {saving ? 'Saving...' : isEdit ? 'Update News' : 'Create News'}
          </button>
        </aside>
      </form>
    </AdminLayout>
  );
}
